import { FileCopyOutlined } from '@material-ui/icons';
import { Alert, Grid, Tooltip, Typography } from '@mui/material';
import MyTable, { MyTableHeader, MyTableBody, MyTableRow, MyTableCol } from 'components/MyTable';
import React, { useCallback } from 'react';
import QuickViewDefault from 'v2/components/Shared/QuickView';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import { InvoiceStatus, SubscriptionStatus, WebhookStatus } from 'v2/interfaces/webhook2';

import type { Webhook, WebhookHistory } from 'v2/interfaces/webhook2';

import { HistoryStatus } from './FilterWebhookHistory';
import styles from '../styles';

type IProps = {
  webhook: WebhookHistory;
  open: boolean;
  handleClose: () => void;
  url: Webhook;
};

export const QuickView = ({ webhook, open, handleClose, url }: IProps) => {
  const classes = styles();

  const indexes = [
    { title: 'Code', ordering: false },
    { title: 'Resposta', ordering: false },
    { title: 'Data', ordering: false },
  ];

  const solveStatusColor = useCallback((status: string): string => {
    if (!status) return 'status-pending';

    const statusInt = Number.parseInt(status);

    if (statusInt >= 200 && statusInt < 300) {
      return 'status-success';
    }

    return 'status-failed';
  }, []);
  const historic = webhook?.sends || [];
  const sent = webhook?.sentData ? JSON.stringify(JSON.parse(webhook.sentData), undefined, 2) : '';

  const [sentData, setSentData] = React.useState<string>();

  const requestCount = parseInt(String(webhook?.count ?? 0));
  const address = webhook?.lastSend?.url || url?.url;

  const handleSentDataCopy = (e: React.SyntheticEvent, w: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w);
    setSentData(w);

    setTimeout(() => {
      setSentData(null);
    }, 2000);
  };

  return (
    <QuickViewDefault title='Detalhes do Webhook' open={open} onClose={handleClose}>
      <Typography sx={{ mb: 1.5 }} color='text.secondary'>
        <strong>URL</strong>
        <div>{address}</div>
      </Typography>
      <Typography sx={{ mb: 1.5 }} color='text.secondary'>
        <Truncate count={50} final='...' title={webhook?.product.title}>
          <strong>Produto</strong>
          <div>{webhook?.product.id !== -1 && `(${webhook?.product.id}) ${webhook?.product.title}`}</div>
        </Truncate>
      </Typography>
      <Typography sx={{ mb: 1.5 }} color='text.secondary'>
        <Truncate count={25} final='...' title={webhook?.customer.name}>
          <strong>Comprador</strong> <div>{webhook?.customer.name}</div>
        </Truncate>
      </Typography>

      {webhook?.type === 'invoice' && (
        <Typography sx={{ mb: 1.5 }} color='text.secondary'>
          <div>
            <strong>Fatura</strong>
            <div>
              {webhook?.entityId} - {InvoiceStatus[webhook.entityStatus] ?? ''}
            </div>
          </div>
        </Typography>
      )}

      {webhook?.type === 'contract' && (
        <Typography sx={{ mb: 1.5 }} color='text.secondary'>
          <div>
            <strong>Contrato</strong>
            <div>
              {webhook?.entityId} - {SubscriptionStatus[webhook?.entityStatus]}
            </div>
          </div>
        </Typography>
      )}

      {requestCount > 0 && (
        <Typography sx={{ mb: 1.5 }} color='text.secondary'>
          <strong>Tentativas:&nbsp;</strong>
          {requestCount}
        </Typography>
      )}

      {url?.state === WebhookStatus.failing && webhook?.status === HistoryStatus.PENDING && (
        <Grid item xs={12} sx={{ marginTop: '24px' }}>
          <Alert severity='warning' style={{ fontSize: 14 }}>
            A sua url está <strong>falhando</strong>, para que este evento seja enviado para a sua URL{' '}
            <strong>(além de todos os outros eventos pendentes no histórico)</strong> é necessário corrigir os erros com a sua integração.
          </Alert>
        </Grid>
      )}

      {webhook?.status === HistoryStatus.ARCHIVED && (
        <Grid item xs={12} sx={{ marginTop: '24px' }}>
          <Alert severity='warning' style={{ fontSize: 14 }}>
            Este evento foi arquivado, para reenviar, basta utilizar o botão de ação de retentativa.
          </Alert>
        </Grid>
      )}

      {webhook?.status === HistoryStatus.FAILED && (
        <Grid item xs={12} sx={{ marginTop: '24px' }}>
          <Alert severity='error' style={{ fontSize: 14 }}>
            Ao enviar esse evento para sua URL, ela retornou o status HTTP <strong>{webhook?.lastSend?.returnStatus}</strong>. Por favor, corrija os
            erros para que na próxima tentativa o evento seja recebido com sucesso.
          </Alert>
        </Grid>
      )}
      <div className={classes.historic}>
        <div className='title'>Histórico de envios</div>
        <div className='content'>
          {historic.length === 0 && <div className='not-found'>Nenhum histórico encontrado.</div>}

          {historic.length > 0 && (
            <div className='table'>
              <MyTable>
                <MyTableHeader index={indexes} />

                <MyTableBody>
                  {historic.map((item) => (
                    <MyTableRow key={item._id}>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={1}>
                        <span className={`${classes.httpStatus} ${solveStatusColor(item.returnStatus)}`}>{item.returnStatus}</span>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={2}>
                        <Truncate count={20} final={'...'}>
                          {item?.returnData || '-'}
                        </Truncate>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 12, sm: 12 }} mobileOrder={3}>
                        {dateFormat(item.created_at, 'dd/MM/yyyy, HH:mm:ss')}
                      </MyTableCol>
                    </MyTableRow>
                  ))}
                </MyTableBody>
              </MyTable>
            </div>
          )}
        </div>
      </div>

      <div className={classes.data}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className='title' style={{ marginRight: '5px' }}>
            Dados enviados
          </div>

          <Tooltip title={sent === sentData ? 'Copiado!' : 'Clique para copiar'}>
            <FileCopyOutlined style={{ cursor: 'pointer' }} onClick={(e) => handleSentDataCopy(e, sent)} />
          </Tooltip>
        </div>
        <div className='content'>
          <pre>{sent}</pre>
        </div>
      </div>
    </QuickViewDefault>
  );
};
